import useConnectors from "@mirage/service-connectors/useConnectors";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { tagged } from "@mirage/service-logging";
import { useOnboardingValues } from "@mirage/service-onboarding/hooks";
import { useConnectConnector } from "@mirage/settings/hooks/connectors/useConnectConnector";
import { getConnectionMetadata } from "@mirage/shared/connectors/util";
import { useCallback, useEffect, useState } from "react";

const logger = tagged("useSlackPrivateConnectorBanner");

const DISMISSED_SLACK_CONNECTOR_SEARCH_BANNER =
  "dismissedSlackConnectorSearchBanner";

export const useSlackPrivateConnectorBanner = () => {
  // Checking banner dismissed state
  const [bannerDismissed, setBannerDismissed] = useState(true);
  const [bannerDismissedDataLoaded, setBannerDismissedDataLoaded] =
    useState(false);
  const { getOnboardingValue, setOnboardingValue } = useOnboardingValues();
  useEffect(() => {
    const getDismissed = async () => {
      try {
        const isDismissed = await getOnboardingValue(
          DISMISSED_SLACK_CONNECTOR_SEARCH_BANNER,
        );
        setBannerDismissed(isDismissed);
        setBannerDismissedDataLoaded(true);
      } catch (error) {
        logger.error(
          "Error getting dismissed slack banner value from getOnboardingValue",
          error,
        );
        // fallback to dismissed state when unknown error occurs
        setBannerDismissed(true);
        setBannerDismissedDataLoaded(true);
      }
    };
    getDismissed();
  }, [getOnboardingValue]);

  const federatedConnectorEnabled = useFeatureFlagValueBool(
    "dash_2025_03_19_slack_federated_connector",
  );
  const { connections, connectors, refreshConnectionsList } = useConnectors();
  const slackConnection = connections.find(
    (connection) =>
      connection.branding?.display_name === "*Slack" ||
      connection.branding?.display_name === "Slack",
  );
  const slackConnector = connectors.find(
    (connector) => connector.id_attrs?.type === "slack",
  );

  const shouldShowSlackBanner = federatedConnectorEnabled && !bannerDismissed;

  const [slackBannerShown, setSlackBannerShown] = useState(false);
  const hideSlackBanner = () => {
    setSlackBannerShown(false);
  };
  const dismissSlackBanner = useCallback(() => {
    hideSlackBanner();
    setOnboardingValue(DISMISSED_SLACK_CONNECTOR_SEARCH_BANNER, true);
    setBannerDismissed(true);
  }, [hideSlackBanner, setOnboardingValue]);

  const connectConnector = useConnectConnector({
    eventProps: { featureLine: "search" },
  });
  const handleConnect = useCallback(async () => {
    if (!slackConnector) return;
    await connectConnector({
      ...slackConnector,
      loading: false,
    });
    // get new connections list to check if new slack connection was successfully created
    // which should result in hiding the banner automatically from the other useEffect
    refreshConnectionsList();
  }, [hideSlackBanner, connectors, connectConnector, refreshConnectionsList]);

  useEffect(() => {
    if (!bannerDismissedDataLoaded) return;
    if (slackConnection) {
      const slackConnectionMetadata = getConnectionMetadata({
        ...slackConnection,
        loading: false,
        syncing: false,
      });
      // Only show the banner when the user has a Slack team connection, but not yet connected to their personal account
      if (
        shouldShowSlackBanner &&
        slackConnectionMetadata.isFederatedConnector &&
        !slackConnectionMetadata.childConnection
      ) {
        setSlackBannerShown(true);
      } else {
        hideSlackBanner();
      }
    }
  }, [slackConnection, shouldShowSlackBanner, bannerDismissedDataLoaded]);

  return {
    slackBannerShown,
    dismissSlackBanner,
    handleConnect,
  };
};
